import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, DateDecoder, field } from '@simonbackx/simple-encoding';
import { GenericBalance } from '../GenericBalance.js';
import { Organization } from '../Organization.js';
import { User } from '../User.js';
import { EmailRecipient } from '../email/Email.js';
import { Replacement } from '../endpoints/EmailRequest.js';
import { compileToInMemoryFilter } from '../filters/InMemoryFilter.js';
import { memberWithRegistrationsBlobInMemoryFilterCompilers } from '../filters/inMemoryFilterDefinitions.js';
import { Member } from './Member.js';
import { MemberPlatformMembership } from './MemberPlatformMembership.js';
import { MemberResponsibilityRecord } from './MemberResponsibilityRecord.js';
import { Registration } from './Registration.js';
export class MemberWithRegistrationsBlob extends Member {
    constructor() {
        super(...arguments);
        this.registrations = [];
        this.users = [];
        this.responsibilities = [];
        this.platformMemberships = [];
        this.balances = [];
    }
    doesMatchFilter(filter) {
        try {
            const compiledFilter = compileToInMemoryFilter(filter, memberWithRegistrationsBlobInMemoryFilterCompilers);
            return compiledFilter(this);
        }
        catch (e) {
            console.error('Error while compiling filter', e, filter);
        }
        return false;
    }
    hasAccount(email) {
        return !!this.users.find(u => u.hasAccount && u.email === email);
    }
    getEmailRecipients(subtypes = null) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        const recipients = [];
        const shared = [];
        shared.push(Replacement.create({
            token: 'firstNameMember',
            value: this.firstName,
        }));
        shared.push(Replacement.create({
            token: 'lastNameMember',
            value: (_a = this.details.lastName) !== null && _a !== void 0 ? _a : '',
        }));
        /* shared.push(Replacement.create({
            token: 'outstandingBalance',
            value: Formatter.price(this.outstandingBalance),
        })); */
        if (this.details.email && (subtypes === null || subtypes.includes('member'))) {
            recipients.push(EmailRecipient.create({
                objectId: this.id,
                memberId: this.id,
                userId: (_e = (_c = (_b = this.users.find(u => u.email === this.details.email)) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : (_d = this.users.find(u => !!this.details.alternativeEmails.find(e => e === u.email))) === null || _d === void 0 ? void 0 : _d.id) !== null && _e !== void 0 ? _e : null,
                firstName: this.details.firstName,
                lastName: this.details.lastName,
                email: this.details.email,
                replacements: [
                    ...shared,
                ],
            }));
        }
        // Always add one without email and user. This one will be visible in the member portal by default if no userId/email match is found
        if (subtypes === null || subtypes.includes('member')) {
            recipients.push(EmailRecipient.create({
                objectId: this.id,
                memberId: this.id,
                firstName: this.details.firstName,
                lastName: this.details.lastName,
                email: null,
                replacements: [
                    ...shared,
                ],
            }));
        }
        if (subtypes === null || subtypes.includes('parents')) {
            for (const parent of this.details.parents) {
                if (parent.email) {
                    recipients.push(EmailRecipient.create({
                        objectId: this.id,
                        memberId: this.id,
                        userId: (_j = (_g = (_f = this.users.find(u => u.email === parent.email)) === null || _f === void 0 ? void 0 : _f.id) !== null && _g !== void 0 ? _g : (_h = this.users.find(u => !!parent.alternativeEmails.find(e => e === u.email))) === null || _h === void 0 ? void 0 : _h.id) !== null && _j !== void 0 ? _j : null,
                        firstName: parent.firstName,
                        lastName: parent.lastName,
                        email: parent.email,
                        replacements: [
                            ...shared,
                        ],
                    }));
                }
            }
        }
        if (subtypes && subtypes.includes('unverified')) {
            for (const unverifiedEmail of this.details.unverifiedEmails) {
                recipients.push(EmailRecipient.create({
                    objectId: this.id,
                    memberId: this.id,
                    userId: (_l = (_k = this.users.find(u => u.email === unverifiedEmail)) === null || _k === void 0 ? void 0 : _k.id) !== null && _l !== void 0 ? _l : null,
                    email: unverifiedEmail,
                    replacements: [
                        ...shared,
                    ],
                }));
            }
        }
        return recipients;
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(Registration) })
], MemberWithRegistrationsBlob.prototype, "registrations", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(User), version: 32 })
], MemberWithRegistrationsBlob.prototype, "users", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(MemberResponsibilityRecord), version: 263 })
], MemberWithRegistrationsBlob.prototype, "responsibilities", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(MemberPlatformMembership), version: 270 })
], MemberWithRegistrationsBlob.prototype, "platformMemberships", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(GenericBalance), version: 375 })
], MemberWithRegistrationsBlob.prototype, "balances", void 0);
export class MembersBlob extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.members = [];
        this.organizations = [];
        /**
         * Timestamp on which the backend constructed this blob
         *
         * This is encoded, so it can be stored locally
         */
        this.receivedAt = new Date();
    }
    markReceivedFromBackend() {
        this.receivedAt = new Date();
    }
    get isStale() {
        return MembersBlob.lastPatchedMembersDate !== null && this.receivedAt < MembersBlob.lastPatchedMembersDate;
    }
    static markAllStale() {
        MembersBlob.lastPatchedMembersDate = new Date();
    }
}
MembersBlob.lastPatchedMembersDate = null;
__decorate([
    field({ decoder: new ArrayDecoder(MemberWithRegistrationsBlob) })
], MembersBlob.prototype, "members", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Organization) })
], MembersBlob.prototype, "organizations", void 0);
__decorate([
    field({ decoder: DateDecoder, version: 329 })
], MembersBlob.prototype, "receivedAt", void 0);
//# sourceMappingURL=MemberWithRegistrationsBlob.js.map